import clsx from 'clsx';
import React, { ReactNode, useEffect } from 'react';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import IconClose from '@components/Images/Rebranding/IconClose';
import TIModal, { TiModalColorScheme } from '@components/TIModal';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, ImpressionElement, click, impression } from '@tracking';

interface ResumeSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  isPausedResumption: boolean;
  isPro: boolean;
  planName: string;
  nextBillingDate: string;
  nextBillingAmount: string;
  onConfirm: () => void;
}

const ResumeSubscriptionModal = ({
  isLoading = false,
  isOpen,
  isPro,
  isPausedResumption,
  planName,
  nextBillingDate,
  nextBillingAmount,
  onClose,
  onConfirm,
}: ResumeSubscriptionModalProps) => {
  const { trackEvent } = useEventTracking();
  const CTAButton = isPro ? ProButton : PrimaryButton;

  const handleConfirmClick = () => {
    onConfirm();
    const clickTarget = isPausedResumption
      ? ClickTarget.ResumePausedSubscription
      : ClickTarget.ResumeStandardSubscription;
    trackEvent(click({ clickTarget, clickText: 'Resume subscription' }));
  };

  const handleModalClose = () => {
    trackEvent(click({ clickTarget: ClickTarget.Close }));
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      trackEvent(impression({ impressionElement: ImpressionElement.ResumeSubscriptionModal }));
    }
  }, [isOpen, trackEvent]);

  const getBodyText = () => {
    if (isPausedResumption) {
      return `You're about to resume your paused subscription. Your access will restart immediately, and billing will resume on ${nextBillingDate}.`;
    }
    return `You're about to resume your subscription. We will charge your payment on file on ${nextBillingDate}.`;
  };

  const getDisclaimerText = (): ReactNode => {
    if (isPausedResumption) {
      return (
        <>
          *Your next payment date reflects your extended renewal period while your subscription was
          paused. Your subscription will renew at {nextBillingAmount}. Prior to each annual renewal,
          The Information will email you about your renewal date and pricing, including any changes
          in cost.
        </>
      );
    }
    return (
      <>
        *Your subscription will renew at {nextBillingAmount}. Prior to each annual renewal, The
        Information will email you about your renewal date and pricing, including any changes in
        cost.
      </>
    );
  };

  return (
    <TIModal
      isOpen={isOpen}
      onClose={handleModalClose}
      disableScrollLock={false}
      customHeader={true}
      isFullScreen={true}
      colorScheme={TiModalColorScheme.dark}
      parentClass="w-full max-w-full xs:max-w-sm sm:max-w-xl md:max-w-5xl lg:max-w-container-lg"
    >
      <div className="absolute right-8 top-8 flex justify-end sm:right-16 sm:top-16">
        <IconClose
          className="h-[26px] w-[26px] cursor-pointer text-white"
          onClick={handleModalClose}
          data-test="close-modal"
          strokeWidth="1"
        />
      </div>
      <div className="mx-auto flex max-w-[632px] flex-col items-center justify-center p-24 text-center xs:p-48 md:px-0 md:py-56">
        <h1 className="m-0 font-suisse-works text-headline-sm text-white sm:text-headline-lg lg:text-headline-xl">
          <span
            className={clsx('mb-8 block font-suisse-intl-mono text-overline-lg font-normal', {
              'text-pro-gold': isPro,
              'text-primary-magenta': !isPro,
            })}
          >
            Confirm Resume
          </span>{' '}
          {planName}
        </h1>
        <p className="mb-0 mt-16 font-suisse-intl-mono text-overline-lg font-normal text-white">
          {getBodyText()}
        </p>
        <p className="mb-0 mt-20 max-w-[550px] font-suisse-intl-mono text-disclosure-md text-overline-md font-normal text-white">
          {getDisclaimerText()}
        </p>
        <CTAButton
          as="button"
          className="mx-auto mt-32 sm:mt-48 sm:max-w-[312px]"
          onClick={handleConfirmClick}
          isLoading={isLoading}
          fullWidth
        >
          Resume subscription
        </CTAButton>
        <p className="mt-16 font-suisse-intl-mono text-overline-md font-normal text-white">
          Total includes applicable sales tax.
        </p>
      </div>
    </TIModal>
  );
};

export default ResumeSubscriptionModal;
